import React from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import { FiArrowLeft } from "react-icons/fi";

/**
 * CrudEditPage - Reusable wrapper for edit/create pages
 *
 * @param {string} title - Page title
 * @param {string} backLink - URL to return to list
 * @param {string} backLabel - Label for back button (default: "Back")
 * @param {array} breadcrumb - Breadcrumb items [{label, to}]
 * @param {function} onSubmit - Form submission handler
 * @param {string} submitLabel - Label for submit button (default: "Save")
 * @param {boolean} isLoading - Show loading state
 * @param {node} children - Form fields
 * @param {node} relatedContent - Optional related items section
 * @param {string} variant - Style variant: "default" or "v4"
 * @param {node} icon - Icon element for V4 header
 */
const CrudEditPage = ({
	title,
	backLink,
	backLabel = "Back",
	breadcrumb = [],
	onSubmit,
	submitLabel = "Save",
	isLoading = false,
	children,
	relatedContent,
	variant = "default",
	icon,
}) => {
	const navigate = useNavigate();

	const handleSubmit = (e) => {
		e.preventDefault();
		if (onSubmit) {
			onSubmit(e);
		}
	};

	const handleCancel = () => {
		if (backLink) {
			navigate(backLink);
		} else {
			navigate(-1);
		}
	};

	const headerActions = backLink && (
		<Link to={backLink} className="btn btn--secondary">
			<FiArrowLeft style={{ marginRight: 6 }} />
			{backLabel}
		</Link>
	);

	const isV4 = variant === "v4";

	const pageClasses = [
		"crud-page",
		"crud-edit",
		isV4 && "crud-page--v4",
	].filter(Boolean).join(" ");

	return (
		<div className={pageClasses}>
			<PageHeader
				title={title}
				breadcrumb={isV4 ? [] : breadcrumb}
				actions={headerActions}
				v4={isV4}
				icon={icon}
			/>

			<form className="crud-edit__form" onSubmit={handleSubmit}>
				{children}

				<div className="crud-edit__actions">
					<button
						type="submit"
						className="btn btn--primary"
						disabled={isLoading}
					>
						{isLoading ? "Saving..." : submitLabel}
					</button>
					<button
						type="button"
						className="btn btn--secondary"
						onClick={handleCancel}
					>
						Cancel
					</button>
				</div>
			</form>

			{relatedContent && (
				<div className="crud-edit__related">
					{relatedContent}
				</div>
			)}
		</div>
	);
};

export default CrudEditPage;
